import { Table, Center, Badge, Group, Button } from "@mantine/core";
import React, { useState } from "react";

export default function ClosedOrdersTableGenerator({ DataObject, title }) {
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // Already-formatted string from upstream
      return (
        <div style={{ textAlign: "center" }}>{String(dateString)}</div>
      );
    }
    const options = {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "Asia/Singapore",
    };
    const formattedDate = date.toLocaleString("en-SG", options);
    const formattedTime = date.toLocaleTimeString("en-SG", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      timeZone: "Asia/Singapore",
    });
    return (
      <div style={{ textAlign: "center" }}>
        {formattedDate}
        <br />
        {formattedTime}
      </div>
    );
  };

  const formatNumber = (value, isQuantity = false) => {
    if (value === null || value === undefined || value === "") return "—";
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numValue)) return value.toString();
    const decimals = isQuantity ? 9 : 2;
    const formattedValue = numValue.toFixed(decimals);
    const parts = formattedValue.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  const renderAction = (v) => {
    const action = String(v || "").toUpperCase();
    const color = action === "BUY" ? "green" : action === "SELL" ? "red" : "gray";
    return <Badge color={color} variant="light">{action}</Badge>;
  };

  const columns = [
    { key: "buyside", header: "Action", render: renderAction },
    { key: "ordertype", header: "Order Type", render: (v) => String(v || "").toUpperCase() },
    { key: "quantity", header: "Quantity", render: (v) => formatNumber(v, true) },
    { key: "price", header: "Price", render: (v) => formatNumber(v) },
    { key: "fillprice", header: "Fill Price", render: (v) => formatNumber(v) },
    { key: "createdat", header: "Created At", render: (v) => (v ? formatDate(v) : "—") },
    { key: "filledat", header: "Filled At", render: (v) => (v ? formatDate(v) : "—") },
  ];

  const ordersPerPage = 7;
  const [sortDir, setSortDir] = useState("desc");
  const [filterType, setFilterType] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const orderTypes = [...new Set(DataObject.map((d) => (d.ordertype || "NORMAL").toUpperCase()))].sort();

  const filteredSorted = DataObject
    .filter((item) => !filterType || (item.ordertype || "NORMAL").toUpperCase() === filterType)
    .filter((item) => !filterAction || (item.buyside || "").toUpperCase() === filterAction)
    .sort((a, b) => {
      const tA = a.createdat ?? 0;
      const tB = b.createdat ?? 0;
      return sortDir === "asc" ? tA - tB : tB - tA;
    });

  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / ordersPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const displayData = filteredSorted.slice((safePage - 1) * ordersPerPage, safePage * ordersPerPage);

  const handleSortDir = (dir) => { setSortDir(dir); setCurrentPage(1); };
  const handleFilterType = (t) => { setFilterType(t); setCurrentPage(1); };
  const handleFilterAction = (a) => { setFilterAction(a); setCurrentPage(1); };

  return (
    <>
    <Group spacing={12} mb={6} style={{ flexWrap: "wrap" }}>
      <Group spacing={4}>
        <span style={{ fontSize: 12, color: "#888" }}>Time:</span>
        <Button size="xs" compact variant={sortDir === "desc" ? "filled" : "subtle"} onClick={() => handleSortDir("desc")}>↓ Newest</Button>
        <Button size="xs" compact variant={sortDir === "asc" ? "filled" : "subtle"} onClick={() => handleSortDir("asc")}>↑ Oldest</Button>
      </Group>
      <Group spacing={4}>
        <span style={{ fontSize: 12, color: "#888" }}>Type:</span>
        <Button size="xs" compact variant={filterType === "" ? "filled" : "subtle"} onClick={() => handleFilterType("")}>All</Button>
        {orderTypes.map((type) => (
          <Button key={type} size="xs" compact variant={filterType === type ? "filled" : "subtle"} onClick={() => handleFilterType(type)}>
            {type.charAt(0) + type.slice(1).toLowerCase()}
          </Button>
        ))}
      </Group>
      <Group spacing={4}>
        <span style={{ fontSize: 12, color: "#888" }}>Action:</span>
        <Button size="xs" compact variant={filterAction === "" ? "filled" : "subtle"} onClick={() => handleFilterAction("")}>All</Button>
        <Button size="xs" compact color="green" variant={filterAction === "BUY" ? "filled" : "subtle"} onClick={() => handleFilterAction("BUY")}>Buy</Button>
        <Button size="xs" compact color="red" variant={filterAction === "SELL" ? "filled" : "subtle"} onClick={() => handleFilterAction("SELL")}>Sell</Button>
      </Group>
    </Group>
    <Table verticalSpacing="xs" highlightOnHover>
      <thead>
        <tr>
          <th colSpan={columns.length}>
            <Center>{title}</Center>
          </th>
        </tr>
        <tr key={"header"}>
          {columns.map((col) => (
            <th key={col.key} style={{ minWidth: "120px" }}>
              <Center>{col.header}</Center>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {displayData.length === 0 ? (
          <tr>
            <td colSpan={columns.length} style={{ textAlign: "center", color: "#888", padding: "12px" }}>
              No orders match the selected filters
            </td>
          </tr>
        ) : (
          displayData.map((item, index) => (
            <tr key={item.orderid || index}>
              {columns.map((col) => (
                <td key={col.key}>
                  <Center>{col.render(item[col.key])}</Center>
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </Table>
    {totalPages > 1 && (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 8 }}>
        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ padding: "4px 10px", cursor: "pointer" }}>Previous</button>
        <span>Page {safePage} / {totalPages}</span>
        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ padding: "4px 10px", cursor: "pointer" }}>Next</button>
      </div>
    )}
    </>
  );
}
